import React, { useState, useEffect } from 'react';
import { 
  Webhook, CheckCircle, XCircle, AlertTriangle, Clock, 
  RefreshCw, Loader2, ChevronDown, ChevronUp
} from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { useAdmin } from '../../context/AdminContext';
import { toast } from 'sonner';
import { format } from 'date-fns';

const WebhookLogItem = ({ log }) => {
  const [expanded, setExpanded] = useState(false);
  
  const statusColors = {
    success: 'bg-green-500/20 text-green-400 border-green-500/30',
    error: 'bg-red-500/20 text-red-400 border-red-500/30',
    processing: 'bg-blue-500/20 text-blue-400 border-blue-500/30'
  };

  const statusIcons = {
    success: CheckCircle,
    error: XCircle,
    processing: Clock
  };

  const StatusIcon = statusIcons[log.status] || AlertTriangle;

  return (
    <div className="bg-slate-700/50 rounded-lg overflow-hidden">
      <div 
        className="p-4 flex items-center justify-between cursor-pointer hover:bg-slate-700"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-center gap-4">
          <div className={`p-2 rounded-lg ${statusColors[log.status]}`}>
            <StatusIcon className="w-4 h-4" />
          </div>
          <div>
            <p className="font-mono text-sm text-white">{log.event_type}</p>
            <p className="text-xs text-slate-400">
              {format(new Date(log.timestamp), 'MMM d, yyyy HH:mm:ss')}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <Badge 
            variant="outline" 
            className={statusColors[log.status]}
          >
            {log.status}
          </Badge>
          {expanded ? (
            <ChevronUp className="w-4 h-4 text-slate-400" />
          ) : (
            <ChevronDown className="w-4 h-4 text-slate-400" />
          )}
        </div>
      </div>
      
      {expanded && (
        <div className="px-4 pb-4 space-y-3 border-t border-slate-600">
          <div className="pt-3">
            <p className="text-xs text-slate-400 mb-1">Event ID</p>
            <p className="font-mono text-sm text-white">{log.event_id}</p>
          </div>
          
          {log.error_message && (
            <div className="p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
              <p className="text-xs text-red-400 mb-1">Error Message</p>
              <p className="text-sm text-red-300">{log.error_message}</p>
              
              {/* Human-friendly guidance */}
              <div className="mt-3 pt-3 border-t border-red-500/20">
                <p className="text-xs text-slate-400 mb-1">Possible Solution</p>
                <p className="text-sm text-slate-300">
                  {getErrorGuidance(log.event_type, log.error_message)}
                </p>
              </div>
            </div>
          )}
          
          {log.payload_preview && (
            <div>
              <p className="text-xs text-slate-400 mb-1">Payload Preview</p>
              <pre className="text-xs text-slate-300 bg-slate-800 p-2 rounded overflow-x-auto max-h-32">
                {log.payload_preview}
              </pre>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

const getErrorGuidance = (eventType, errorMessage) => {
  const errorLower = errorMessage?.toLowerCase() || '';
  
  if (errorLower.includes('signature')) {
    return 'Check that STRIPE_WEBHOOK_SECRET is correctly configured in environment variables.';
  }
  if (errorLower.includes('timeout')) {
    return 'The webhook processing timed out. Check database connectivity and server performance.';
  }
  if (errorLower.includes('not found')) {
    return 'The referenced resource was not found. This may indicate a data sync issue.';
  }
  if (eventType.includes('payment')) {
    return 'Review the payment integration and ensure Stripe API keys are valid.';
  }
  
  return 'Review server logs for more details. Contact support if the issue persists.';
};

const AdminWebhooksPage = () => {
  const { getWebhookLogs } = useAdmin();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = async () => {
    try {
      const data = await getWebhookLogs(20);
      setLogs(data.logs || []);
    } catch (error) {
      toast.error('Failed to load webhook logs');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = async () => {
    setRefreshing(true);
    await fetchLogs();
    setRefreshing(false);
    toast.success('Logs refreshed');
  };

  const successCount = logs.filter(l => l.status === 'success').length;
  const errorCount = logs.filter(l => l.status === 'error').length;

  if (loading) {
    return (
      <div className="h-full flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-trust-blue" />
      </div>
    );
  }

  return (
    <div className="p-8" data-testid="admin-webhooks">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl font-bold text-white mb-2">
            Webhooks / Status
          </h1>
          <p className="text-slate-400">
            Monitor incoming webhook events and system status
          </p>
        </div>
        <Button
          onClick={handleRefresh}
          disabled={refreshing}
          variant="outline"
          className="border-slate-600 text-slate-300 hover:text-white hover:bg-slate-700"
          data-testid="refresh-webhooks"
        >
          {refreshing ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <RefreshCw className="w-4 h-4 mr-2" />
          )}
          Refresh
        </Button>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-slate-700 rounded-lg">
              <Webhook className="w-5 h-5 text-trust-blue" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{logs.length}</p>
              <p className="text-sm text-slate-400">Total Events</p>
            </div>
          </div>
        </div>
        
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-green-500/20 rounded-lg">
              <CheckCircle className="w-5 h-5 text-green-400" />
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{successCount}</p>
              <p className="text-sm text-slate-400">Successful</p>
            </div>
          </div>
        </div>
        
        <div className="bg-slate-800 rounded-xl border border-slate-700 p-4">
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${errorCount > 0 ? 'bg-red-500/20' : 'bg-slate-700'}`}>
              {errorCount > 0 ? (
                <XCircle className="w-5 h-5 text-red-400" />
              ) : (
                <CheckCircle className="w-5 h-5 text-slate-400" />
              )}
            </div>
            <div>
              <p className="text-2xl font-bold text-white">{errorCount}</p>
              <p className="text-sm text-slate-400">Errors</p>
            </div>
          </div>
        </div>
      </div>

      {/* Webhook Logs */}
      <div className="bg-slate-800 rounded-xl border border-slate-700 p-6">
        <h2 className="font-heading text-xl font-semibold text-white mb-4 flex items-center gap-2">
          <Clock className="w-5 h-5 text-trust-blue" />
          Recent Webhook Events
        </h2>

        {logs.length === 0 ? (
          <div className="text-center py-12">
            <Webhook className="w-12 h-12 text-slate-600 mx-auto mb-4" />
            <p className="text-slate-400">No webhook events recorded yet</p>
            <p className="text-slate-500 text-sm mt-1">
              Events will appear here when Stripe sends webhooks
            </p>
          </div>
        ) : (
          <div className="space-y-2">
            {logs.map((log) => (
              <WebhookLogItem key={log.log_id} log={log} />
            ))}
          </div>
        )}
      </div>

      {/* Help Section */}
      <div className="mt-6 p-4 bg-slate-800/50 rounded-xl border border-slate-700">
        <h3 className="font-medium text-white mb-2">Troubleshooting Tips</h3>
        <ul className="text-sm text-slate-400 space-y-1">
          <li>• <strong className="text-slate-300">Signature Errors:</strong> Verify STRIPE_WEBHOOK_SECRET in environment variables</li>
          <li>• <strong className="text-slate-300">Timeout Errors:</strong> Check database and server performance</li>
          <li>• <strong className="text-slate-300">Not Found Errors:</strong> May indicate data sync issues between Stripe and database</li>
          <li>• <strong className="text-slate-300">No Events:</strong> Ensure webhook URL is configured in Stripe Dashboard</li>
        </ul>
      </div>
    </div>
  );
};

export default AdminWebhooksPage;
